<template>
  <div class="container-fluid">
    <Breadcrumbs title="Configuración" main="Configuración" />
    <div class="card border-0 shadow-sm">
      <div class="card-body p-4">
        <h5 class="card-title mb-4">{{ textos.titulo }}</h5>
        <p class="text-muted mb-4">{{ textos.descripcion }}</p>

        <div v-for="(destinatario, index) in destinatariosConsultados" :key="destinatario.id" class="mb-3">
          <form class="row g-2 align-items-end" @submit.prevent="validarYGuardar(destinatario.id)">
            <div class="col-md-9">
              <label class="form-label small text-muted">Destinatario {{ index + 1 }}</label>
              <input
                v-model="inputs[destinatario.id].data"
                type="email"
                class="form-control"
                :class="{ 'is-invalid': inputs[destinatario.id].errorMessage }"
                placeholder="correo@ejemplo.com"
              />
              <div v-if="inputs[destinatario.id].errorMessage" class="invalid-feedback d-block">
                {{ inputs[destinatario.id].errorMessage }}
              </div>
            </div>
            <div class="col-md-3 d-flex gap-1">
              <button type="submit" class="btn btn-primary btn-sm" title="Guardar">
                <vue-feather type="check" size="16" />
              </button>
              <button
                type="button"
                class="btn btn-danger btn-sm"
                title="Eliminar"
                @click="eliminarDestinatario(destinatario.id)"
              >
                <vue-feather type="trash-2" size="16" />
              </button>
            </div>
          </form>
        </div>

        <form v-if="mostrarNuevo" class="row g-2 align-items-end mb-3" @submit.prevent="validarNuevo">
          <div class="col-md-9">
            <label class="form-label small text-muted">Nuevo destinatario</label>
            <input
              v-model="nuevoCorreo"
              type="email"
              class="form-control"
              :class="{ 'is-invalid': nuevoError }"
              placeholder="correo@ejemplo.com"
            />
            <div v-if="nuevoError" class="invalid-feedback d-block">{{ nuevoError }}</div>
          </div>
          <div class="col-md-3">
            <button type="submit" class="btn btn-primary btn-sm">
              <vue-feather type="plus" size="16" class="me-1" />
              Agregar
            </button>
          </div>
        </form>

        <button
          type="button"
          :class="['btn btn-sm', mostrarNuevo ? 'btn-outline-secondary' : 'btn-outline-primary']"
          @click="mostrarNuevo = !mostrarNuevo"
        >
          <vue-feather :type="mostrarNuevo ? 'x' : 'mail'" size="16" class="me-1" />
          {{ mostrarNuevo ? "Cancelar" : "Agregar destinatario" }}
        </button>

        <div v-if="destinatariosConsultados.length === 0 && !mostrarNuevo" class="mt-4 text-muted small">
          No hay destinatarios. Agrega al menos uno para recibir reportes por correo.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Swal from "sweetalert2";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TEXTOS = {
  carga: {
    titulo: "Destinatarios carga de imágenes",
    descripcion:
      "Los correos que agregues aquí recibirán el reporte de carga de imágenes.",
  },
  gs1: {
    titulo: "Destinatarios reporte GS1",
    descripcion:
      "Los correos que agregues aquí recibirán el reporte diario con los productos sin foto en la APP que se buscaron y cargaron desde GS1.",
  },
};

export default {
  name: "Configuracion",
  props: {
    tipo: {
      type: String,
      default: "carga",
      validator: (v) => ["carga", "gs1"].includes(v),
    },
  },
  data() {
    return {
      inputs: {},
      nuevoCorreo: "",
      nuevoError: "",
      mostrarNuevo: false,
    };
  },
  computed: {
    ...mapState("destinatarios", ["destinatarios"]),
    destinatariosConsultados() {
      return this.destinatarios || [];
    },
    textos() {
      return TEXTOS[this.tipo] || TEXTOS.carga;
    },
  },
  watch: {
    tipo() {
      this.nuevoCorreo = "";
      this.nuevoError = "";
      this.mostrarNuevo = false;
      this.consultarDestinatarios(this.tipo);
    },
    destinatarios: {
      handler(val) {
        const lista = val || [];
        lista.forEach((d) => {
          this.inputs[d.id] = { data: d.email || "", errorMessage: "" };
        });
      },
      immediate: true,
    },
  },
  mounted() {
    this.consultarDestinatarios(this.tipo);
  },
  methods: {
    ...mapActions("destinatarios", ["guardarDestinatario", "consultarDestinatarios", "borrarDestinatario"]),
    validarCorreo(valor) {
      if (!valor || !EMAIL_REGEX.test(String(valor).trim())) return "Correo inválido";
      return "";
    },
    validarYGuardar(id) {
      const obj = this.inputs[id];
      if (!obj) return;
      const err = this.validarCorreo(obj.data);
      obj.errorMessage = err;
      if (err) return;
      this.guardarActualizar(obj.data, id);
    },
    validarNuevo() {
      const err = this.validarCorreo(this.nuevoCorreo);
      this.nuevoError = err;
      if (err) return;
      this.guardarActualizar(this.nuevoCorreo.trim(), null);
    },
    async guardarActualizar(correo, id) {
      try {
        await this.guardarDestinatario({ correo: correo.trim(), id, tipo: this.tipo });
        this.nuevoCorreo = "";
        this.nuevoError = "";
        this.mostrarNuevo = false;
        Swal.fire({ icon: "success", title: id ? "Actualizado" : "Agregado", timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: e.code || "Error",
          text: e.msg || "No se pudo guardar.",
        });
      }
    },
    async eliminarDestinatario(id) {
      const ok = await Swal.fire({
        icon: "question",
        title: "¿Eliminar destinatario?",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
      });
      if (!ok.isConfirmed) return;
      try {
        await this.borrarDestinatario({ id, tipo: this.tipo });
        Swal.fire({ icon: "success", title: "Eliminado", timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: e.code || "Error",
          text: e.msg || "No se pudo eliminar.",
        });
      }
    },
  },
};
</script>
